import { useState, useEffect, lazy, Suspense } from 'react';
import type { Card } from '@mtg-deck-tool/shared';
import { api, ApiClientError } from '../../lib/api-client.js';
import { useCollectionStore } from '../../stores/collection-store.js';
import { useDeckStore } from '../../stores/deck-store.js';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { MoxfieldImportFlow } from './MoxfieldImportFlow.js';
import { ManaSymbolIcons } from '../shared/ManaSymbolIcons.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { getStrategyBadgeStyle } from './strategy-badge.js';

// Code-split the heavy tab views — each loads on first navigation to its tab,
// keeping them out of the initial bundle.
const AnalyticsDashboard = lazy(() =>
  import('./AnalyticsDashboard.js').then((m) => ({ default: m.AnalyticsDashboard }))
);
const CommanderSwipeView = lazy(() =>
  import('./CommanderSwipeView.js').then((m) => ({ default: m.CommanderSwipeView }))
);
const WantToBuildView = lazy(() =>
  import('./WantToBuildView.js').then((m) => ({ default: m.WantToBuildView }))
);
const SetReviewView = lazy(() =>
  import('./SetReviewView.js').then((m) => ({ default: m.SetReviewView }))
);

/** Lightweight centered spinner shown while a lazy tab view's chunk loads. */
function TabSuspenseFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

interface CollectionViewProps {
  onOpenEditor: () => void;
}

export function CollectionView({ onOpenEditor }: CollectionViewProps) {
  const { decks, isLoading, error, fetchCollection, deleteDeck, deleteDecks, syncDeck, fetchAnalytics } = useCollectionStore();
  const [showImport, setShowImport] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const handlePurgeCache = async () => {
    setIsPurging(true);
    try {
      await api.purgeCache();
      await fetchCollection();
      await fetchAnalytics(true);
    } catch (err) {
      console.error('[purge-cache-error]', err);
    } finally {
      setIsPurging(false);
    }
  };
  const initializeGame = usePlaytesterStore((s) => s.initializeGame);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [playtestingId, setPlaytestingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [colorFilter, setColorFilter] = useState<string[]>([]);
  const [colorMatchMode, setColorMatchMode] = useState<'includes' | 'exact'>('includes');
  const [commanderCards, setCommanderCards] = useState<Map<string, Card>>(new Map());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'decks' | 'analytics' | 'discovery' | 'wishlist' | 'set-review'>('decks');
  const isMobile = useIsMobile();
  const [discoveryColors, setDiscoveryColors] = useState<string | null>(null);
  const [discoveryStrategy, setDiscoveryStrategy] = useState<string | null>(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedDeckIds, setSelectedDeckIds] = useState<string[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      await deleteDecks(selectedDeckIds);
      setSelectedDeckIds([]);
      setIsSelectMode(false);
    } catch (err) {
      console.error('[bulk-delete-error]', err);
    } finally {
      setIsBulkDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  // 1. Fetch collection on mount
  useEffect(() => {
    void fetchCollection();
  }, [fetchCollection]);

  // 2. Proactively resolve commander card details to render beautiful card art portraits
  useEffect(() => {
    if (decks.length === 0) return;

    const allCommanderNames = Array.from(
      new Set(decks.flatMap((d) => d.commanderNames))
    );

    // Only resolve ones we don't already have cached in state
    const unresolved = allCommanderNames.filter((name) => !commanderCards.has(name.toLowerCase()));
    if (unresolved.length === 0) return;

    api
      .resolveCards(unresolved)
      .then(({ found }) => {
        setCommanderCards((prev) => {
          const next = new Map(prev);
          for (const card of found) {
            next.set(card.name.toLowerCase(), card);
            // Also map dual/split cards by primary face name
            if (card.name.includes(' // ')) {
              next.set(card.name.split(' // ')[0]!.toLowerCase(), card);
            }
          }
          return next;
        });
      })
      .catch((err) => {
        console.error('[commander-resolution-error]', err);
      });
  }, [decks, commanderCards]);

  // Handle opening a deck in the editor
  const handleOpenDeck = async (id: string) => {
    try {
      const fullDeck = await api.getCollectionDeck(id);
      
      // Directly hydrate the deck builder store
      useDeckStore.setState({
        deckId: fullDeck.id,
        deckName: fullDeck.name,
        entries: fullDeck.entries,
        sideboard: fullDeck.sideboard,
        moxfieldDeckId: fullDeck.moxfieldDeckId,
        colorIdentity: fullDeck.colorIdentity,
        tagProfile: null,       // Recalculated automatically
        tagHierarchy: null,
        isProfileStale: true,
        importGeneration: (useDeckStore.getState().importGeneration ?? 0) + 1,
      });

      // Switch view to editor
      onOpenEditor();
    } catch (err: any) {
      console.error('[load-deck-error]', err);
    }
  };

  // Sync deck with Moxfield
  const handleSyncDeck = async (id: string) => {
    setSyncingId(id);
    try {
      await syncDeck(id);
    } catch (err) {
      console.error('[sync-deck-error]', err);
    } finally {
      setSyncingId(null);
    }
  };

  // Open a deck in the playtester
  const handlePlaytest = async (id: string) => {
    setPlaytestingId(id);
    try {
      const fullDeck = await api.getCollectionDeck(id);
      initializeGame(fullDeck, 'collection');
    } catch (err) {
      console.error('[playtest-error]', err);
    } finally {
      setPlaytestingId(null);
    }
  };

  // Create a fresh new blank deck in the collection and open it
  const handleCreateNewDeck = () => {
    const newId = `deck-${Date.now()}`;
    // Save minimal blank deck
    api.saveCollectionDeck({
      id: newId,
      name: 'New Commander Deck',
      colorIdentity: [],
      commanderNames: [],
      cardCount: 0,
      entries: [],
      sideboard: [],
    })
    .then(() => fetchCollection())
    .then(() => handleOpenDeck(newId))
    .catch((err) => {
      console.error('[create-deck-error]', err);
    });
  };

  // Toggle color filter
  const toggleColorFilter = (color: string) => {
    setColorFilter((prev) => {
      const next = prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color];
      if (next.length === 0) {
        setColorMatchMode('includes');
      }
      return next;
    });
  };

  // Filtered decks list
  const filteredDecks = decks.filter((deck) => {
    const matchesSearch =
      deck.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deck.commanderNames.some((c: string) => c.toLowerCase().includes(searchQuery.toLowerCase()));

    if (colorFilter.length === 0) return matchesSearch;

    if (colorMatchMode === 'exact') {
      // Must contain exactly the selected colors
      return (
        matchesSearch &&
        deck.colorIdentity.length === colorFilter.length &&
        colorFilter.every((col) => deck.colorIdentity.includes(col as any))
      );
    } else {
      // Must contain at least all of the selected colors
      return matchesSearch && colorFilter.every((col) => deck.colorIdentity.includes(col as any));
    }
  });

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-gray-950 text-gray-100">
      <div className="px-6 pt-8 shrink-0">
        {/* ── Dashboard Top Header ────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-800/80 pb-6 mb-8">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-300">
            Deck Collection
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            Store and manage your commander portfolio, analyze strategy gaps, and sync from Moxfield
          </p>
        </div>

        <div className="flex items-center gap-3">
          {activeTab === 'decks' && decks.length > 0 && (
            <>
              {isSelectMode ? (
                <>
                  <button
                    onClick={() => {
                      const allIds = filteredDecks.map((d) => d.id);
                      const allSelected = allIds.every((id) => selectedDeckIds.includes(id));
                      setSelectedDeckIds(allSelected ? [] : allIds);
                    }}
                    className="px-3.5 py-2 text-sm font-semibold border border-gray-800 hover:border-gray-700 bg-gray-900/40 hover:bg-gray-800/40 text-gray-300 rounded-xl transition-all"
                  >
                    {filteredDecks.map((d) => d.id).every((id) => selectedDeckIds.includes(id))
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>

                  <button
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    disabled={selectedDeckIds.length === 0}
                    className="px-4 py-2 text-sm font-semibold bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl shadow-lg shadow-red-600/10 transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Selected ({selectedDeckIds.length})
                  </button>

                  <button
                    onClick={() => {
                      setIsSelectMode(false);
                      setSelectedDeckIds([]);
                    }}
                    className="px-4 py-2 text-sm font-semibold border border-gray-800 hover:border-gray-700 bg-gray-900/20 text-gray-400 hover:text-gray-200 rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsSelectMode(true)}
                  className="px-4 py-2 text-sm font-semibold border border-gray-800 hover:border-gray-700 bg-gray-900/30 hover:bg-gray-800/40 text-gray-300 rounded-xl transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Manage Decks
                </button>
              )}
            </>
          )}

          {!isSelectMode && (
            <>
              <button
                onClick={handleCreateNewDeck}
                className="px-4 py-2 text-sm font-semibold border border-orange-500/20 hover:border-orange-500 bg-orange-950/20 hover:bg-orange-900/20 text-orange-400 rounded-xl transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Deck
              </button>
              
              <button
                onClick={() => setShowImport(true)}
                className="px-5 py-2 text-sm font-semibold bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white rounded-xl shadow-lg shadow-orange-500/10 transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import Moxfield Decks
              </button>

              <button
                onClick={handlePurgeCache}
                disabled={isPurging}
                className="px-4 py-2 text-sm font-semibold border border-red-900/40 hover:border-red-500 bg-red-950/20 hover:bg-red-900/20 text-red-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-all flex items-center gap-2"
                title="Clears all cached tag profiles, commander themes, and synergies to force a clean re-classification of all decks."
              >
                {isPurging ? (
                  <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
                Clear Cache
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Sub-navigation Tabs ────────────────────────────────── */}
      <div className="flex border-b border-gray-900 mb-8 gap-1 select-none overflow-x-auto">
        <button
          onClick={() => setActiveTab('decks')}
          className={`shrink-0 whitespace-nowrap pb-3 px-5 text-sm font-semibold border-b-2 transition-all duration-200 ${
            activeTab === 'decks'
              ? 'border-orange-500 text-orange-400 font-extrabold'
              : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-800/50'
          }`}
        >
          My Decks
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`shrink-0 whitespace-nowrap pb-3 px-5 text-sm font-semibold border-b-2 transition-all duration-200 ${
            activeTab === 'analytics'
              ? 'border-orange-500 text-orange-400 font-extrabold'
              : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-800/50'
          }`}
        >
          Portfolio Analytics
        </button>
        <button
          onClick={() => setActiveTab('discovery')}
          className={`shrink-0 whitespace-nowrap pb-3 px-5 text-sm font-semibold border-b-2 transition-all duration-200 ${
            activeTab === 'discovery'
              ? 'border-orange-500 text-orange-400 font-extrabold'
              : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-800/50'
          }`}
        >
          Commander Discovery
        </button>
        <button
          onClick={() => setActiveTab('wishlist')}
          className={`shrink-0 whitespace-nowrap pb-3 px-5 text-sm font-semibold border-b-2 transition-all duration-200 ${
            activeTab === 'wishlist'
              ? 'border-orange-500 text-orange-400 font-extrabold'
              : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-800/50'
          }`}
        >
          Want to Build
        </button>
        <button
          onClick={() => setActiveTab('set-review')}
          className={`shrink-0 whitespace-nowrap pb-3 px-5 text-sm font-semibold border-b-2 transition-all duration-200 ${
            activeTab === 'set-review'
              ? 'border-orange-500 text-orange-400 font-extrabold'
              : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-800/50'
          }`}
        >
          Set Review
        </button>
      </div>
    </div>

    {/* ── Tab Views Wrapper ────────────────────────────────────── */}
    <div className="flex-1 min-h-0 relative">
        {/* 1. Analytics Dashboard Tab */}
        <div className={`absolute inset-0 overflow-y-auto px-6 pb-8 ${activeTab === 'analytics' ? 'block' : 'hidden'}`}>
          {activeTab === 'analytics' && (
            <Suspense fallback={<TabSuspenseFallback />}>
              <AnalyticsDashboard
                onSelectColorGap={(colors) => {
                  setDiscoveryColors(colors);
                  setDiscoveryStrategy(null);
                  setActiveTab('discovery');
                }}
                onSelectStrategyGap={(strategy) => {
                  setDiscoveryStrategy(strategy);
                  setDiscoveryColors(null);
                  setActiveTab('discovery');
                }}
                onOpenDeck={handleOpenDeck}
                onSelectColorCoverage={(colors) => {
                  setColorFilter(colors);
                  setColorMatchMode('exact');
                  setActiveTab('decks');
                }}
              />
            </Suspense>
          )}
        </div>

        {/* 2. Commander Discovery Tab */}
        <div className={`absolute inset-0 overflow-y-auto px-6 pb-8 ${activeTab === 'discovery' ? 'block' : 'hidden'}`}>
          {activeTab === 'discovery' && (
            <Suspense fallback={<TabSuspenseFallback />}>
              <CommanderSwipeView
                initialColors={discoveryColors}
                initialStrategy={discoveryStrategy}
                onClearFilters={() => {
                  setDiscoveryColors(null);
                  setDiscoveryStrategy(null);
                }}
              />
            </Suspense>
          )}
        </div>

        {/* 3. Want to Build Tab */}
        <div className={`absolute inset-0 overflow-y-auto px-6 pb-8 ${activeTab === 'wishlist' ? 'block' : 'hidden'}`}>
          {activeTab === 'wishlist' && (
            <Suspense fallback={<TabSuspenseFallback />}>
              <WantToBuildView />
            </Suspense>
          )}
        </div>

        {/* 5. Set Review Tab — full-screen overlay on mobile, inline panel on desktop */}
        {activeTab === 'set-review' && (
          isMobile ? (
            <div className="fixed inset-0 z-50 bg-gray-950 flex flex-col">
              <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-800 shrink-0">
                <button
                  type="button"
                  onClick={() => setActiveTab('decks')}
                  className="px-2.5 py-1 text-xs font-semibold border border-gray-700 text-gray-300 rounded-lg hover:border-gray-500 transition-all"
                >
                  ‹ Close
                </button>
                <span className="text-sm font-semibold text-gray-200">Set Review</span>
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <Suspense fallback={<TabSuspenseFallback />}>
                  <SetReviewView commanderCards={commanderCards} />
                </Suspense>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 overflow-y-auto pb-8">
              <Suspense fallback={<TabSuspenseFallback />}>
                <SetReviewView commanderCards={commanderCards} />
              </Suspense>
            </div>
          )
        )}

        {/* 4. My Decks Tab */}
        <div className={`absolute inset-0 overflow-y-auto px-6 pb-8 ${activeTab === 'decks' ? 'block' : 'hidden'}`}>
          <div>
          {/* ── Filters and Controls ───────────────────────────────── */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-gray-900/30 border border-gray-800/60 p-4 rounded-2xl backdrop-blur-md">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-500">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search decks by name or commander..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-950/60 border border-gray-800 rounded-xl pl-10 pr-4 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-300"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Color Filtering & View Mode Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wider mr-2">Filter Colors:</span>
                {['W', 'U', 'B', 'R', 'G'].map((color) => {
                  const active = colorFilter.includes(color);
                  return (
                    <button
                      key={color}
                      onClick={() => toggleColorFilter(color)}
                      className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                        active
                          ? 'bg-orange-500/25 border-orange-500 shadow-md shadow-orange-500/10'
                          : 'bg-gray-950/40 border-gray-800/80 hover:border-gray-700'
                      }`}
                    >
                      <ManaSymbolIcons colors={[color as any]} size={16} />
                    </button>
                  );
                })}
                {colorFilter.length > 0 && (
                  <div className="flex items-center gap-1.5 ml-3 pl-3 border-l border-gray-800">
                    {/* Includes vs Exactly Toggle */}
                    <div className="flex items-center gap-0.5 bg-gray-950/80 p-0.5 rounded-lg border border-gray-800/60 select-none">
                      <button
                        onClick={() => setColorMatchMode('includes')}
                        className={`px-2 py-1 rounded text-[10px] font-semibold transition ${
                          colorMatchMode === 'includes'
                            ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                            : 'text-gray-500 hover:text-gray-300 border border-transparent'
                        }`}
                        title="Include decks with these colors"
                      >
                        Including
                      </button>
                      <button
                        onClick={() => setColorMatchMode('exact')}
                        className={`px-2 py-1 rounded text-[10px] font-semibold transition ${
                          colorMatchMode === 'exact'
                            ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                            : 'text-gray-500 hover:text-gray-350 border border-transparent'
                        }`}
                        title="Match exact colors only"
                      >
                        Exactly
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        setColorFilter([]);
                        setColorMatchMode('includes');
                      }}
                      className="text-xs text-orange-400 hover:text-orange-355 font-semibold transition-colors ml-1"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-gray-950/80 p-0.5 rounded-xl border border-gray-800/60 sm:ml-auto select-none shrink-0 self-start sm:self-auto">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-lg text-xs font-semibold transition ${
                    viewMode === 'list'
                      ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                      : 'text-gray-500 hover:text-gray-300 border border-transparent'
                  }`}
                  title="List View"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-lg text-xs font-semibold transition ${
                    viewMode === 'grid'
                      ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                      : 'text-gray-500 hover:text-gray-350 border border-transparent'
                  }`}
                  title="Grid View"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* ── Main Loading / Error states ──────────────────────────── */}
          {isLoading && decks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Loading collection...</p>
            </div>
          ) : error ? (
            <div className="p-6 bg-red-950/20 border border-red-900/40 text-red-300 text-sm rounded-2xl flex items-center gap-3">
              <svg className="w-6 h-6 shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="font-semibold">Error Loading Collection</div>
                <div className="text-xs mt-0.5">{error}</div>
              </div>
            </div>
          ) : filteredDecks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center border border-dashed border-gray-800 rounded-3xl bg-gray-900/10 px-6">
              <span className="text-4xl">🃏</span>
              <h3 className="text-base font-bold text-gray-300 mt-4">
                {decks.length === 0 ? 'Your collection is empty' : 'No matching decks found'}
              </h3>
              <p className="text-xs text-gray-500 mt-2 max-w-sm">
                {decks.length === 0
                  ? 'Start by creating a blank deck or bulk importing your deck portfolio from Moxfield!'
                  : 'Try clearing your active filters or query search.'}
              </p>
              {decks.length === 0 && (
                <button
                  onClick={() => setShowImport(true)}
                  className="mt-5 px-5 py-2 text-sm font-semibold bg-orange-600 hover:bg-orange-500 text-white rounded-xl transition-all"
                >
                  Import from Moxfield
                </button>
              )}
            </div>
          ) : viewMode === 'list' ? (
            /* ── Decks List Layout ──────────────────────────────────── */
            <div className="flex flex-col gap-3">
              {filteredDecks.map((deck) => {
                const primaryCommanderName = deck.commanderNames[0];
                const commanderCard = primaryCommanderName ? commanderCards.get(primaryCommanderName.toLowerCase()) : null;
                const cropUri = commanderCard?.imageUris?.artCrop ??
                                commanderCard?.cardFaces?.[0]?.imageUris?.artCrop;

                const isSyncing = syncingId === deck.id;
                const isSelected = selectedDeckIds.includes(deck.id);

                return (
                  <div
                    key={deck.id}
                    className={`group relative flex items-center justify-between p-3.5 bg-gray-900/20 hover:bg-gray-900/40 border rounded-2xl transition-all duration-200 cursor-pointer ${
                      isSelectMode
                        ? isSelected
                          ? 'border-orange-500 bg-orange-500/[0.01] shadow-sm font-semibold'
                          : 'border-gray-850 opacity-60 hover:opacity-85 hover:border-gray-800'
                        : 'border-gray-850 hover:border-orange-500/25 hover:shadow-sm'
                    }`}
                    onClick={() => {
                      if (isSelectMode) {
                        setSelectedDeckIds((prev) =>
                          isSelected ? prev.filter((id) => id !== deck.id) : [...prev, deck.id]
                        );
                      } else {
                        void handleOpenDeck(deck.id);
                      }
                    }}
                  >
                    {/* Left side: Select status + Portrait + Info */}
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      {isSelectMode && (
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                          isSelected
                            ? 'bg-orange-500 border-orange-400 text-white'
                            : 'bg-black/40 border-gray-700 text-transparent'
                        }`}>
                          {isSelected && (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      )}

                      {/* Art Portrait */}
                      <div className="w-11 h-11 rounded-xl overflow-hidden border border-gray-850/80 bg-gray-950 shrink-0">
                        {cropUri ? (
                          <img
                            src={cropUri}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-900">
                            <span className="text-lg opacity-40">🃏</span>
                          </div>
                        )}
                      </div>

                      {/* Deck Info */}
                      <div className="min-w-0 flex-1 pr-4">
                        <div className="flex items-center gap-3">
                          <h3 className="font-bold text-gray-100 group-hover:text-orange-400 transition-colors text-sm truncate" title={deck.name}>
                            {deck.name}
                          </h3>
                          <span className="text-[10px] text-gray-500 font-bold shrink-0">
                            {deck.cardCount} Cards
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {deck.commanderNames.length > 0 ? (
                            <span className="text-[11px] text-gray-400 truncate max-w-[200px]" title={deck.commanderNames.join(' & ')}>
                              {deck.commanderNames.join(' & ')}
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-550 italic">No Commander</span>
                          )}

                          {/* Strategy badges */}
                          {deck.primaryStrategy && (
                            <div className="flex flex-wrap gap-1 shrink-0 ml-2 select-none">
                              {[deck.primaryStrategy, deck.secondaryStrategy, deck.thirdStrategy, deck.fourthStrategy]
                                .filter(Boolean)
                                .map((strat) => (
                                  <span key={strat} className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${getStrategyBadgeStyle(strat!).bg} ${getStrategyBadgeStyle(strat!).text} ${getStrategyBadgeStyle(strat!).border}`}>
                                    {strat}
                                  </span>
                                ))
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right side: Colors + Synced Status + Action buttons */}
                    <div className="flex items-center gap-6 shrink-0">
                      {/* Color identity */}
                      <ManaSymbolIcons colors={deck.colorIdentity} size={15} />

                      {/* Sync/Local status */}
                      <div className="text-[10px] text-gray-500 hidden sm:block w-[100px] text-right">
                        {deck.syncedAt ? (
                          <span>Synced {new Date(deck.syncedAt).toLocaleDateString()}</span>
                        ) : (
                          <span>Local deck</span>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {!isSelectMode && (
                          <button
                            onClick={() => void handlePlaytest(deck.id)}
                            disabled={playtestingId === deck.id}
                            className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-600/30 hover:border-orange-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Playtest this deck"
                          >
                            {playtestingId === deck.id ? 'Loading...' : 'Playtest'}
                          </button>
                        )}

                        {!isSelectMode && deck.moxfieldDeckId && (
                          <button
                            onClick={() => void handleSyncDeck(deck.id)}
                            disabled={isSyncing}
                            className="p-1.5 hover:bg-gray-850 rounded-lg text-gray-400 hover:text-orange-400 transition-colors"
                            title="Sync with Moxfield"
                          >
                            <svg className={`w-4 h-4 ${isSyncing ? 'animate-spin text-orange-400' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.213 6H16" />
                            </svg>
                          </button>
                        )}

                        {!isSelectMode && (
                          <button
                            onClick={() => setConfirmDeleteId(deck.id)}
                            className="p-1.5 hover:bg-gray-850 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                            title="Delete Deck"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── Decks Grid Layout ──────────────────────────────────── */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredDecks.map((deck) => {
                // Find Scryfall card data for commander if resolved to show crop art
                const primaryCommanderName = deck.commanderNames[0];
                const commanderCard = primaryCommanderName ? commanderCards.get(primaryCommanderName.toLowerCase()) : null;
                const cropUri = commanderCard?.imageUris?.artCrop ??
                                commanderCard?.cardFaces?.[0]?.imageUris?.artCrop;

                const isSyncing = syncingId === deck.id;

                const isSelected = selectedDeckIds.includes(deck.id);

                return (
                  <div
                    key={deck.id}
                    className={`group relative flex flex-col h-[290px] bg-gradient-to-b from-gray-900/70 to-gray-950 border rounded-2xl overflow-hidden shadow-lg transition-all duration-300 cursor-pointer ${
                      isSelectMode
                        ? isSelected
                          ? 'border-orange-500 bg-orange-500/[0.02] shadow-orange-500/[0.02] ring-1 ring-orange-500/30 font-semibold'
                          : 'border-gray-850 opacity-60 hover:opacity-80 hover:border-gray-800'
                        : 'border-gray-850 hover:border-orange-500/40 hover:shadow-orange-500/[0.03] hover:-translate-y-1'
                    }`}
                    onClick={() => {
                      if (isSelectMode) {
                        setSelectedDeckIds((prev) =>
                          isSelected ? prev.filter((id) => id !== deck.id) : [...prev, deck.id]
                        );
                      } else {
                        void handleOpenDeck(deck.id);
                      }
                    }}
                  >
                    {/* Floating check status circle when in Select Mode */}
                    {isSelectMode && (
                      <div className={`absolute top-3 left-3 z-30 w-6 h-6 rounded-full border flex items-center justify-center backdrop-blur-md shadow-md transition-all duration-300 ${
                        isSelected
                          ? 'bg-orange-500 border-orange-400 text-white'
                          : 'bg-black/60 border-gray-700 text-transparent hover:border-gray-550'
                      }`}>
                        {isSelected && (
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    )}

                    {/* 1. Glassy Art Crop Header */}
                    <div className="relative h-[130px] overflow-hidden border-b border-gray-900 bg-gray-950 shrink-0">
                      {cropUri ? (
                        <img
                          src={cropUri}
                          alt=""
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
                          <span className="text-3xl opacity-20">🃏</span>
                        </div>
                      )}
                      {/* Subtle vignette gradient cover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-black/30" />

                      {/* Colors & Counts overlaid */}
                      <div className="absolute bottom-2.5 left-3.5 right-3.5 flex items-center justify-between">
                        <ManaSymbolIcons colors={deck.colorIdentity} size={16} />
                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-200 bg-black/60 border border-gray-800/80 px-2 py-0.5 rounded-full backdrop-blur-md">
                          {deck.cardCount} Cards
                        </span>
                      </div>
                    </div>

                    {/* 2. Content Body */}
                    <div className="p-4 flex-1 flex flex-col justify-between min-w-0">
                      <div className="min-w-0">
                        <h3 className="font-bold text-gray-100 group-hover:text-orange-400 transition-colors text-sm truncate" title={deck.name}>
                          {deck.name}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1 truncate" title={deck.commanderNames.join(' & ') || 'No Commander'}>
                          {deck.commanderNames.length > 0 ? (
                            <>
                              <span className="text-orange-500 text-[10px] font-bold mr-1">&#x2694;</span>
                              {deck.commanderNames.join(' & ')}
                            </>
                          ) : (
                            <span className="text-gray-500 italic">No Commander designated</span>
                          )}
                        </p>

                        {/* Strategy badges */}
                        {deck.primaryStrategy && (
                          <div className="flex flex-wrap gap-1 mt-2.5 select-none">
                            {[deck.primaryStrategy, deck.secondaryStrategy, deck.thirdStrategy, deck.fourthStrategy]
                              .filter(Boolean)
                              .map((strat) => (
                                <span key={strat} className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${getStrategyBadgeStyle(strat!).bg} ${getStrategyBadgeStyle(strat!).text} ${getStrategyBadgeStyle(strat!).border}`}>
                                  {strat}
                                </span>
                              ))
                            }
                          </div>
                        )}
                      </div>

                      {/* 3. Action Buttons & Metadata Footer */}
                      <div className="flex items-center justify-between border-t border-gray-900 pt-3 mt-2 shrink-0">
                        <div className="text-[10px] text-gray-500">
                          {deck.syncedAt ? (
                            <span title={`Synced at ${new Date(deck.syncedAt).toLocaleString()}`}>
                              Synced {new Date(deck.syncedAt).toLocaleDateString()}
                            </span>
                          ) : (
                            <span>Local deck</span>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          {!isSelectMode && (
                            <button
                              onClick={() => void handlePlaytest(deck.id)}
                              disabled={playtestingId === deck.id}
                              className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-600/30 hover:border-orange-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Playtest this deck"
                            >
                              {playtestingId === deck.id ? 'Loading...' : 'Playtest'}
                            </button>
                          )}

                          {!isSelectMode && deck.moxfieldDeckId && (
                            <button
                              onClick={() => void handleSyncDeck(deck.id)}
                              disabled={isSyncing}
                              className="p-1.5 hover:bg-gray-850 rounded-lg text-gray-400 hover:text-orange-400 transition-colors"
                              title="Sync with Moxfield"
                            >
                              <svg className={`w-4 h-4 ${isSyncing ? 'animate-spin text-orange-400' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.213 6H16" />
                              </svg>
                            </button>
                          )}
                          
                          {!isSelectMode && (
                            <button
                              onClick={() => setConfirmDeleteId(deck.id)}
                              className="p-1.5 hover:bg-gray-850 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                              title="Delete Deck"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* ── Moxfield Import Modal Flow ───────────────────────────── */}
      {showImport && (
        <MoxfieldImportFlow
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false);
            void fetchCollection();
          }}
        />
      )}

      {/* ── Elegant Delete Confirmation Dialog ───────────────────── */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm" onClick={() => setConfirmDeleteId(null)}>
          <div
            className="bg-gray-900 border border-gray-800 rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-gray-100">Delete Commander Deck?</h3>
            <p className="text-xs text-gray-400">
              Are you sure you want to delete this deck? This action is permanent and will completely purge the deck and all cached tag profiles.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 text-xs font-semibold text-gray-400 hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmDeleteId) {
                    void deleteDeck(confirmDeleteId);
                    setConfirmDeleteId(null);
                  }
                }}
                className="px-4 py-2 text-xs font-semibold bg-red-600 hover:bg-red-500 rounded-xl text-white transition-colors"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Elegant Bulk Delete Confirmation Dialog ──────────────── */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm" onClick={() => setShowBulkDeleteConfirm(false)}>
          <div
            className="bg-gray-900 border border-gray-800 rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-gray-100 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Bulk Delete Decks?
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Are you sure you want to permanently delete <strong className="text-red-400">{selectedDeckIds.length}</strong> selected decks? This action is permanent and will completely purge them and all cached tag profiles.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                disabled={isBulkDeleting}
                className="px-4 py-2 text-xs font-semibold text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={() => { void handleBulkDelete(); }}
                disabled={isBulkDeleting}
                className="px-5 py-2 text-xs font-semibold bg-red-600 hover:bg-red-500 disabled:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-white transition-colors flex items-center gap-1.5"
              >
                {isBulkDeleting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Confirm Mass Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
